import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

/**
 * Pharmacy catalogue self-service API.
 *
 * - listStock(pharmacyId)   — fetch a pharmacy's current PharmacyStock rows.
 * - downloadTemplate(opts)  — fetch the CSV template as a Blob; caller
 *                             triggers download via createObjectURL.
 * - bulkUpload({ file, pharmacyId }) — multipart POST of the edited CSV.
 *                             Returns the server's summary JSON.
 * - updateStockRow(id, patch) — inline edit a single PharmacyStock row.
 * - deleteStockRow(id)      — remove a stock row (pharmacy stops stocking).
 */

/**
 * Admin convenience: list every pharmacy so the catalogue page can let
 * an admin pick which pharmacy's catalogue to manage. Pharmacy staff
 * don't need this — they're scoped to their own pharmacy on the server.
 */
export async function listPharmacies() {
  const { data } = await api.get(endpoints.pharmacyList)
  return unwrapList(data)
}

// A pharmacy's whole catalogue is rendered client-side (filter + inline edit)
// with no page controls, so we ask the server for the full set in one request.
// DefaultPageNumberPagination caps page_size at 1000 (max_page_size), which
// covers every real catalogue with headroom; without this the list is silently
// capped at the 50-row default and out-of-stock rows past page 1 stay hidden.
const CATALOGUE_PAGE_SIZE = 1000

export async function listStock({ pharmacyId, search = '', page = 1 } = {}) {
  const params = { page, page_size: CATALOGUE_PAGE_SIZE }
  if (pharmacyId) params.pharmacy = pharmacyId
  if (search?.trim()) params.search = search.trim()
  const { data } = await api.get(endpoints.pharmacyStock, { params })
  return unwrapList(data)
}

/**
 * Download the formulary template. Returns a Blob the caller saves via
 * a hidden <a download>. Pass `otcOnly: true` to limit the template to
 * OTC drugs (~213 rows) instead of the full formulary (~1826 rows).
 */
export async function downloadTemplate({ otcOnly = false } = {}) {
  const url = otcOnly
    ? `${endpoints.pharmacyStockTemplate}?include_otc_only=true`
    : endpoints.pharmacyStockTemplate
  const res = await api.get(url, { responseType: 'blob' })
  return res.data
}

/**
 * Upload an edited CSV. Returns the server's response:
 *   { summary: { rows_processed, stock_created, stock_updated,
 *                proposals_created, errors },
 *     errors:    [{row, error}, ...],
 *     proposals: [{row, medication_name, proposal_id}, ...] }
 */
export async function bulkUpload({ file, pharmacyId }) {
  const fd = new FormData()
  fd.append('file', file)
  fd.append('pharmacy_id', String(pharmacyId))
  const { data } = await api.post(endpoints.pharmacyStockBulkUpload, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * Catalogue-wide low / out-of-stock counts for the banner. Server counts
 * over the pharmacy's full catalogue, not just the loaded page.
 *   -> { low, out, total }
 */
export async function lowStockSummary({ pharmacyId } = {}) {
  const params = {}
  if (pharmacyId) params.pharmacy = pharmacyId
  const { data } = await api.get(`${endpoints.pharmacyStock}low_stock_summary/`, { params })
  return data
}

/**
 * Create a single new PharmacyStock row (one drug into the pharmacy's
 * catalogue). Powers the "+ Add drug" modal — the fast per-drug path vs
 * the bulk CSV upload.
 *
 * `payload` fields:
 *   drug            — global Drug id (required; picked via CatalogueAutocomplete)
 *   pharmacy        — pharmacy id (optional for pharmacy staff — the server
 *                     forces this to the caller's active pharmacy; required
 *                     for admins)
 *   brand_name      — pharmacy's specific brand (defaults to '' — "generic")
 *   price           — decimal, PGK
 *   stock_quantity  — non-negative integer, default 0
 *   reorder_level   — non-negative integer, default 0
 *   is_available    — boolean, default true
 *   notes           — optional 255-char string
 *
 * Server 400 with a JSON error is bubbled up as an axios error — most common:
 * UniqueConstraint(pharmacy, drug) violation when the drug is already in
 * the catalogue. The modal shows a friendly "You already stock this drug"
 * on that specific error.
 */
export async function createStockRow(payload) {
  const { data } = await api.post(endpoints.pharmacyStock, payload)
  return data
}

export async function updateStockRow(id, patch) {
  const { data } = await api.patch(`${endpoints.pharmacyStock}${id}/`, patch)
  return data
}

export async function deleteStockRow(id) {
  await api.delete(`${endpoints.pharmacyStock}${id}/`)
}

/**
 * Upload (or replace) the product image for a *global* Drug. Admin-only —
 * the DrugViewSet permission is `ReadOnlyForAuthenticated | IsAdminRole`,
 * so non-admins will 403. Pharmacy staff see the image via the read-only
 * `drug_image_url` pass-through on PharmacyStockSerializer.
 *
 * Multipart PATCH so we don't have to send the rest of the Drug fields.
 * Pass `null` to clear the image.
 */
export async function setDrugImage(drugId, file) {
  const fd = new FormData()
  if (file) {
    fd.append('image', file)
  } else {
    fd.append('image', '')
  }
  const { data } = await api.patch(`${endpoints.drugSearch}${drugId}/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}
